'use client'

import * as React from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { CheckIcon, SearchIcon } from 'lucide-react'

import cn from '@config/tailwind/cn'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './dialog'
import { InputGroup, InputGroupAddon } from './input-group'

function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot='command'
      className={cn(
        'ui:bg-neutrals-white ui:text-neutrals-black ui:rounded-xl! ui:p-1 ui:flex ui:size-full ui:flex-col ui:overflow-hidden',
        className,
      )}
      {...props}
    />
  )
}
function CommandDialog({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = false,
  ...props
}: Omit<React.ComponentProps<typeof Dialog>, 'children'> & {
  title?: string
  description?: string
  className?: string
  showCloseButton?: boolean
  children: React.ReactNode
}) {
  return (
    <Dialog {...props}>
      <DialogHeader className='sr-only'>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={cn('ui:rounded-xl! ui:top-1/3 ui:translate-y-0 ui:overflow-hidden ui:p-0', className)}
        showCloseButton={showCloseButton}
      >
        {children}
      </DialogContent>
    </Dialog>
  )
}
function CommandInput({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div data-slot='command-input-wrapper' className='p-1 pb-0'>
      <InputGroup className='ui:bg-main-blue-20/30 ui:border-main-blue-75/30 ui:h-8! ui:rounded-lg! ui:shadow-none! ui:*:data-[slot=input-group-addon]:pl-2!'>
        <CommandPrimitive.Input
          data-slot='command-input'
          className={cn(
            'ui:w-full ui:text-sm ui:outline-hidden ui:disabled:cursor-not-allowed ui:disabled:opacity-50',
            className,
          )}
          {...props}
        />
        <InputGroupAddon>
          <SearchIcon className='ui:size-4 ui:shrink-0 ui:opacity-50' />
        </InputGroupAddon>
      </InputGroup>
    </div>
  )
}
function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot='command-list'
      className={cn(
        'ui:no-scrollbar ui:max-h-72 ui:scroll-py-1 ui:outline-none ui:overflow-x-hidden ui:overflow-y-auto',
        className,
      )}
      {...props}
    />
  )
}
function CommandEmpty({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot='command-empty'
      className={cn('ui:py-6 ui:text-center ui:text-sm', className)}
      {...props}
    />
  )
}
function CommandGroup({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot='command-group'
      className={cn(
        'ui:text-neutrals-black ui:[&_[cmdk-group-heading]]:ui:text-neutrals-n-20 ui:overflow-hidden ui:p-1 ui:[&_[cmdk-group-heading]]:ui:px-2 ui:[&_[cmdk-group-heading]]:ui:py-1.5 ui:[&_[cmdk-group-heading]]:ui:text-xs ui:[&_[cmdk-group-heading]]:ui:font-medium',
        className,
      )}
      {...props}
    />
  )
}
function CommandSeparator({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot='command-separator'
      className={cn('ui:bg-neutrals-n-20 ui:-mx-1 ui:h-px', className)}
      {...props}
    />
  )
}
function CommandItem({ className, children, ...props }: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot='command-item'
      className={cn(
        "ui:data-selected:bg-neutrals-n-20 ui:data-selected:text-neutrals-black ui:data-selected:*:[svg]:text-neutrals-black ui:relative ui:flex ui:cursor-default ui:items-center ui:gap-2 ui:rounded-sm ui:px-2 ui:py-1.5 ui:text-sm ui:outline-hidden ui:select-none ui:[&_svg:not([class*='size-'])]:size-4 ui:[[data-slot=dialog-content]_&]:ui:rounded-lg! group/command-item ui:data-[disabled=true]:pointer-events-none ui:data-[disabled=true]:opacity-50 ui:[&_svg]:pointer-events-none ui:[&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      {children}
      <CheckIcon className='ui:ml-auto ui:opacity-0 ui:group-has-data-[slot=command-shortcut]/command-item:hidden ui:group-data-[checked=true]/command-item:opacity-100' />
    </CommandPrimitive.Item>
  )
}
function CommandShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot='command-shortcut'
      className={cn(
        'ui:text-neutrals-n-75 ui:group-data-selected/command-item:ui:text-neutrals-black ui:ml-auto ui:text-xs ui:tracking-widest',
        className,
      )}
      {...props}
    />
  )
}
export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
